// web/creatureDraw.ts — turn an assembled CreatureSprite (the gene→sprite SPEC
// from src/sim/creatureSprite.ts) into a drawn, LAYERED biological bug on the
// canvas, so evolution is visible WITHOUT opening menus: bigger Size ⇒ bigger
// body, Defense ⇒ armour plates, Movement ⇒ wings + longer legs, Combat ⇒
// mandibles, Toxicity ⇒ a venom blob, Sensing ⇒ antennae.
//
// SPLIT BY DESIGN: the gene→geometry MAPPING (`buildCreatureGeometry`) is a PURE
// function with no DOM dependency, so it is unit-testable headless (no canvas).
// `paintCreature` is the only DOM-touching half. This mirrors the engine's
// "data first, render second" discipline — the math is deterministic and tested;
// the painter just strokes/fills the primitives the math produced.
//
// NOT in the tick, NOT in any state hash: rendering is debug/UI only. Geometry is
// a pure function of the (cached) sprite + a cosmetic seed, so it can never
// perturb sim determinism. Thousands of tiny bugs use render.ts's LOD dot path;
// this full-detail path runs only when a cell is large enough to read.

import {
  BODY_PART,
  ATTACHMENT,
} from '../src/types.ts';
// Types only — keep the engine sprite + DOM types out of the bundle's runtime.
import type { CreatureSprite } from '../src/sim/creatureSprite.ts';

// ---------------------------------------------------------------------------
// Geometry — plain data, LOCAL pixel space around an origin (0,0). The painter
// translates to the entity's screen centre. All sizes derive from the sprite's
// integer scales (percent of baseline, 100 = baseline) times a passed baseRadius.
// ---------------------------------------------------------------------------

/** One body segment ellipse (Abdomen / Thorax / Head), or a venom blob. */
export interface BodyEllipse {
  /** Horizontal radius in local px. */
  rx: number;
  /** Vertical radius in local px. */
  ry: number;
  /** Vertical centre offset from origin (negative = up / toward head). */
  cyOff: number;
  /** Fill role: the colony body colour, or the toxic venom hue. */
  fill: 'body' | 'venom';
  /** Lightness nudge for this segment, in [-1, 1]-ish steps (painter scales it). */
  shade: number;
}

/** A line segment (legs, mandibles, antennae) in local px. */
export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** A wing ellipse flanking the thorax. */
export interface Wing {
  cxOff: number;
  cyOff: number;
  rx: number;
  ry: number;
}

/** A single armour plate bar drawn across the thorax/abdomen. */
export interface ArmorPlate {
  cyOff: number;
  halfW: number;
  thick: number;
}

/**
 * The full drawable description of one creature, in local pixel space. Built once
 * per draw at detail (cheap; the LOD dot path skips it entirely). Ordering of the
 * arrays is the back→front composite order the painter walks.
 */
export interface CreatureGeometry {
  /** Body segments back→front: Abdomen, Thorax, Head (venom appended last). */
  bodies: BodyEllipse[];
  /** Three leg pairs (six strokes) splayed from the thorax. */
  legs: Segment[];
  /** Wings flanking the thorax (0 or 2). */
  wings: Wing[];
  /** Mandible prongs at the head (0 or 2). */
  mandibles: Segment[];
  /** Armour plate bars over the thorax (0..4). */
  armor: ArmorPlate[];
  /** Venom blob at the abdomen tail, or null. */
  venom: { cyOff: number; r: number } | null;
  /** Antennae from the head top (0 or 2). */
  antennae: Segment[];
  /**
   * Glow intensities carried from the sprite (render-only luminance cues; 0 = none):
   * `venom` is the sickly halo on the venom sac (Toxicity), `body` is the cyan/teal
   * bioluminescence on the abdomen (Regen). Normalised to ~[0,1] from the sprite's
   * integer 0..GLOW_MAX, so the painter scales radius/alpha directly.
   */
  glow: { venom: number; body: number };
  /** Overall scale of the whole creature (sprite.baseScale, 100 = baseline). */
  overall: { scale: number };
}

// ---------------------------------------------------------------------------
// Mapping constants — keep the bug compact and readable at small cell sizes.
// ---------------------------------------------------------------------------

/** A part scale percent → a multiplier on baseRadius (100% = 1.0). */
function pct(scalePct: number): number {
  return scalePct / 100;
}

/**
 * Limbs part-scale percent at/above which a creature grows a 4th leg pair (a
 * legibility threshold: only clearly fast creatures gain extra legs). The Limbs
 * channel sits ~105% at baseline and climbs ~9%/Movement, so this is ~Movement 5+.
 */
const LEG_EXTRA_PAIR_PCT = 150;

/**
 * The sprite's integer glow scale's upper bound (mirrors GLOW_MAX in
 * creatureSprite.ts). Geometry normalises the raw 0..GLOW_MAX intensity into
 * ~[0,1] so the painter can scale a halo's radius/alpha directly.
 */
const GLOW_MAX = 200;

/** Normalise a sprite glow intensity (0..GLOW_MAX) into a 0..1 painter factor. */
function glowFactor(intensity: number): number {
  if (intensity <= 0) return 0;
  const t = intensity / GLOW_MAX;
  return t > 1 ? 1 : t;
}

/**
 * Deterministic bounded cosmetic jitter. Same (seed, salt) ⇒ same value in
 * [-amp, +amp]. Pure integer-mix → float; no allocation, no Math.random.
 */
function jitter(seed: number, salt: number, amp: number): number {
  let z = (((seed >>> 0) + Math.imul(salt + 1, 0x9e3779b9)) >>> 0) >>> 0;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  z = (z ^ (z >>> 15)) >>> 0;
  // Map to [-amp, +amp] across 1001 steps for a smooth small spread.
  const t = (z % 1001) / 1000; // [0,1]
  return (t * 2 - 1) * amp;
}

// ---------------------------------------------------------------------------
// THE GENE → GEOMETRY MAPPING (pure; no DOM).
// ---------------------------------------------------------------------------

/**
 * Build a `CreatureGeometry` from an assembled `sprite`. `baseRadius` sets the
 * nominal half-size of a 100%-scale part in screen px; `seed` (e.g. the entity id)
 * adds bounded, deterministic cosmetic jitter so individuals differ by ~±1px
 * without changing WHICH parts/attachments are present (structure is fixed by the
 * sprite). Same sprite + same seed ⇒ deep-equal geometry. PURE + allocation is
 * fine here: only called at detail, never for the thousands of LOD dots.
 */
export function buildCreatureGeometry(
  sprite: CreatureSprite,
  baseRadius: number,
  seed: number,
): CreatureGeometry {
  // Overall genome size scales the whole bug; clamp the contribution so a runaway
  // genome can't blow past the cell (the sprite already clamps baseScale to <=300).
  const overall = sprite.baseScale / 100;
  const r = baseRadius * overall;

  // --- Body segments: Head (top, small), Thorax (mid), Abdomen (bottom, large). ---
  const headPct = pct(sprite.partScale[BODY_PART.Head] as number);
  const thoPct = pct(sprite.partScale[BODY_PART.Thorax] as number);
  const abdPct = pct(sprite.partScale[BODY_PART.Abdomen] as number);

  // Segment radii. Abdomen is the widest+tallest (the gut); head the smallest.
  const headRx = r * 0.42 * headPct + jitter(seed, 1, 0.5);
  const headRy = r * 0.40 * headPct + jitter(seed, 2, 0.5);
  const thoRx = r * 0.50 * thoPct + jitter(seed, 3, 0.5);
  const thoRy = r * 0.46 * thoPct;
  const abdRx = r * 0.58 * abdPct + jitter(seed, 4, 0.6);
  const abdRy = r * 0.66 * abdPct + jitter(seed, 5, 0.6);

  // Stack them head→thorax→abdomen down the body axis; offsets keep them touching.
  const headCy = -(thoRy + headRy * 0.7);
  const thoCy = 0;
  const abdCy = thoRy + abdRy * 0.7;

  const bodies: BodyEllipse[] = [
    { rx: abdRx, ry: abdRy, cyOff: abdCy, fill: 'body', shade: -0.12 },
    { rx: thoRx, ry: thoRy, cyOff: thoCy, fill: 'body', shade: 0.0 },
    { rx: headRx, ry: headRy, cyOff: headCy, fill: 'body', shade: 0.14 },
  ];

  // --- Legs: leg pairs from the thorax; reach + COUNT scale with the Limbs part
  //     (which Movement drives). A baseline insect has 3 pairs; a fast creature
  //     (Limbs grown well past baseline) sprouts a 4th pair, so "more Movement"
  //     reads as both LONGER and MORE legs without ballooning off-screen. ---
  const limbPct = pct(sprite.partScale[BODY_PART.Limbs] as number);
  const legReach = r * 0.85 * limbPct;
  const legs: Segment[] = [];
  // Anchor rows across the thorax; each emits a left + right leg. A 4th (rear)
  // row is added only for clearly fast creatures (Limbs >= LEG_EXTRA_PAIR_PCT).
  const extraPair = (sprite.partScale[BODY_PART.Limbs] as number) >= LEG_EXTRA_PAIR_PCT;
  const rows = extraPair
    ? [-thoRy * 0.55, -thoRy * 0.18, thoRy * 0.18, thoRy * 0.55]
    : [-thoRy * 0.5, 0, thoRy * 0.5];
  const mid = (rows.length - 1) / 2; // centre index ⇒ splay front rows forward, rear back
  for (let i = 0; i < rows.length; i++) {
    const ay = (rows[i] as number) + thoCy;
    // Splay angle: front legs reach forward, rear legs backward (centred on `mid`).
    const dy = (i - mid) * legReach * 0.45 + jitter(seed, 10 + i, 0.6);
    const ax = thoRx * 0.7;
    legs.push({ x1: -ax, y1: ay, x2: -ax - legReach, y2: ay + dy });
    legs.push({ x1: ax, y1: ay, x2: ax + legReach, y2: ay + dy });
  }

  // --- Wings: present iff the sprite mounts them; flank the thorax. ---
  const wings: Wing[] = [];
  if ((sprite.attachCount[ATTACHMENT.Wings] as number) > 0) {
    const wScale = (sprite.attachSize[ATTACHMENT.Wings] as number) / 100;
    const wRx = r * 0.55 * wScale;
    const wRy = r * 0.30 * wScale;
    const wcx = thoRx + wRx * 0.7;
    wings.push({ cxOff: -wcx, cyOff: thoCy - thoRy * 0.2, rx: wRx, ry: wRy });
    wings.push({ cxOff: wcx, cyOff: thoCy - thoRy * 0.2, rx: wRx, ry: wRy });
  }

  // --- Mandibles: present iff mounted; two prongs at the head top, splayed wider
  //     for an "aggressive" head variant (partVariant[Head] > 0). ---
  const mandibles: Segment[] = [];
  if ((sprite.attachCount[ATTACHMENT.Mandibles] as number) > 0) {
    const mScale = (sprite.attachSize[ATTACHMENT.Mandibles] as number) / 100;
    const mLen = r * 0.42 * mScale;
    const aggressive = (sprite.partVariant[BODY_PART.Head] as number) > 0;
    const splay = aggressive ? 0.9 : 0.5;
    const topY = headCy - headRy;
    const baseX = headRx * 0.6;
    mandibles.push({ x1: -baseX, y1: topY, x2: -baseX - mLen * splay, y2: topY - mLen });
    mandibles.push({ x1: baseX, y1: topY, x2: baseX + mLen * splay, y2: topY - mLen });
  }

  // --- Armour: up to 4 plate bars across the thorax (more plates = more Defense). ---
  const armor: ArmorPlate[] = [];
  const plateCount = Math.min(sprite.attachCount[ATTACHMENT.Armor] as number, 4);
  if (plateCount > 0) {
    const aScale = (sprite.attachSize[ATTACHMENT.Armor] as number) / 100;
    const span = thoRy * 1.4; // stack the bars across the thorax height
    const thick = Math.max(1, r * 0.1 * aScale);
    for (let p = 0; p < plateCount; p++) {
      const t = plateCount === 1 ? 0.5 : p / (plateCount - 1);
      const cyOff = thoCy - span * 0.5 + t * span;
      armor.push({ cyOff, halfW: thoRx * (0.9 + 0.06 * aScale), thick });
    }
  }

  // --- Venom: a toxic blob at the abdomen tail iff a venom sac is mounted. ---
  let venom: { cyOff: number; r: number } | null = null;
  if ((sprite.attachCount[ATTACHMENT.VenomSac] as number) > 0) {
    const vScale = (sprite.attachSize[ATTACHMENT.VenomSac] as number) / 100;
    venom = { cyOff: abdCy + abdRy * 0.85, r: Math.max(1.5, r * 0.22 * vScale) };
  }

  // --- Antennae: two thin lines from the head top iff mounted. ---
  const antennae: Segment[] = [];
  if ((sprite.attachCount[ATTACHMENT.Antennae] as number) > 0) {
    const anScale = (sprite.attachSize[ATTACHMENT.Antennae] as number) / 100;
    const aLen = r * 0.5 * anScale;
    const topY = headCy - headRy * 0.8;
    const baseX = headRx * 0.4;
    antennae.push({ x1: -baseX, y1: topY, x2: -baseX - aLen * 0.5, y2: topY - aLen });
    antennae.push({ x1: baseX, y1: topY, x2: baseX + aLen * 0.5, y2: topY - aLen });
  }

  return {
    bodies,
    legs,
    wings,
    mandibles,
    armor,
    venom,
    antennae,
    glow: {
      venom: glowFactor(sprite.venomGlow),
      body: glowFactor(sprite.bodyGlow),
    },
    overall: { scale: sprite.baseScale },
  };
}

// ---------------------------------------------------------------------------
// THE PAINTER (DOM). Strokes/fills the geometry primitives at (cx, cy). The
// colony colour stays DOMINANT so colonies remain distinguishable at a glance.
// ---------------------------------------------------------------------------

/** Default toxic-green venom hue (used on non-green colonies). */
const VENOM_FILL = '#7CFC4A';
const VENOM_STROKE = '#3a7a18';
/**
 * Colony-AWARE fallback venom hue: an acid yellow-green for when the colony body
 * colour is itself green-dominant (e.g. the green colony `#5dd65d`), where the
 * default toxic-green reads with low contrast. Shifted toward yellow (more red,
 * less blue) so the venom blob still pops as "poison" but separates from a green
 * body, paired with a near-black outline for a hard edge on any colour.
 */
const VENOM_FILL_ACID = '#d8ff2e';
const VENOM_STROKE_ACID = '#1f2a00';

/**
 * Pick the venom fill/stroke for a body colour. Pure function of the colony rgb
 * (the same input the painter already has) — no DOM, no allocation beyond the
 * returned literal pair. When the body is clearly GREEN-DOMINANT (green is the
 * top channel by a margin AND not near-grey), the toxic-green blob would blend in,
 * so swap to the acid yellow-green + hard outline; otherwise keep the classic
 * toxic green. Other colony colours (cyan/red/amber/violet/pink) keep the default,
 * so their contrast is unchanged.
 */
function venomColorsFor(rgb: [number, number, number]): { fill: string; stroke: string } {
  const [r, g, b] = rgb;
  // Green-dominant ⇒ green leads both other channels by a clear margin (so a
  // balanced teal/cyan, where blue is close to green, is NOT treated as green).
  const greenDominant = g > r + 28 && g > b + 28;
  if (greenDominant) return { fill: VENOM_FILL_ACID, stroke: VENOM_STROKE_ACID };
  return { fill: VENOM_FILL, stroke: VENOM_STROKE };
}
/** Wings are translucent so the body reads through them. */
const WING_FILL = 'rgba(255,255,255,0.22)';
const WING_STROKE = 'rgba(255,255,255,0.4)';

// --- Glow hues (render-only). RGB triples; the painter builds the gradient stops
//     from these so the alpha can scale with the (normalised) glow intensity. ---
/** Venom-sac glow: a sickly acid green-yellow ("poison" luminance). */
const VENOM_GLOW_RGB = '124,255,40';
/** Body bioluminescence: a cool cyan/teal ("living tissue" luminance). */
const BODY_GLOW_RGB = '70,224,208';

/**
 * Paint one additive radial-glow disc centred at (x,y): a soft `rgb` halo that
 * fades to transparent at `radius`, with peak alpha scaled by `factor` (0..1).
 * Uses 'lighter' compositing so overlapping glows brighten (bioluminescence reads
 * as emitted light, not a flat fill). Allocation: one gradient object per call —
 * only on the detail path (never the LOD-dot thousands), matching the rest of the
 * full-detail painter. Caller owns ctx.save/restore around the composite switch.
 */
function paintGlow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  rgb: string,
  factor: number,
): void {
  if (factor <= 0 || radius <= 0) return;
  const peak = 0.18 + 0.42 * factor; // brighter with the gene, capped well under 1
  const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
  g.addColorStop(0, `rgba(${rgb},${peak.toFixed(3)})`);
  g.addColorStop(0.55, `rgba(${rgb},${(peak * 0.45).toFixed(3)})`);
  g.addColorStop(1, `rgba(${rgb},0)`);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Parse a `#rgb`/`#rrggbb` colour into [r,g,b]. Falls back to mid-grey on an
 * unexpected format so the painter never throws. (Called a handful of times per
 * drawn bug — full-detail path only.)
 */
function parseHex(hex: string): [number, number, number] {
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  if (h.length !== 6) return [136, 136, 136];
  const n = Number.parseInt(h, 16);
  if (!Number.isFinite(n)) return [136, 136, 136];
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** Shade a colony colour lighter (shade>0) or darker (shade<0); returns rgb(). */
function shadeColor(rgb: [number, number, number], shade: number): string {
  const k = shade; // ~[-0.5, 0.5]
  const mix = (c: number): number => {
    const target = k >= 0 ? 255 : 0;
    const v = Math.round(c + (target - c) * Math.min(0.6, Math.abs(k)));
    return v < 0 ? 0 : v > 255 ? 255 : v;
  };
  return `rgb(${mix(rgb[0])},${mix(rgb[1])},${mix(rgb[2])})`;
}

/** Stroke an ellipse outline path (helper; assumes ctx already translated). */
function ellipsePath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0.5, rx), Math.max(0.5, ry), 0, 0, Math.PI * 2);
}

/**
 * Paint `geom` at screen centre (cx, cy) using `bodyColor` (the colony colour) as
 * the dominant body fill. Back→front: legs, wings, abdomen, venom, thorax, armour,
 * head, mandibles, antennae. DOM-only; pure geometry was already computed.
 *
 * `rotation` (radians, default 0) turns the whole bug about its centre so it can
 * face its travel direction — geometry is authored nose-UP, so a rotation of
 * `atan2(vx, -vy)` points the head along velocity (see web/watch.ts).
 */
export function paintCreature(
  ctx: CanvasRenderingContext2D,
  geom: CreatureGeometry,
  cx: number,
  cy: number,
  bodyColor: string,
  rotation = 0,
): void {
  const rgb = parseHex(bodyColor);
  ctx.save();
  ctx.translate(cx, cy);
  if (rotation !== 0) ctx.rotate(rotation);

  const lineBase = Math.max(0.6, (geom.bodies[1]?.rx ?? 4) * 0.14);

  // Legs (behind the body).
  ctx.strokeStyle = shadeColor(rgb, -0.35);
  ctx.lineWidth = lineBase;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const l of geom.legs) {
    ctx.moveTo(l.x1, l.y1);
    ctx.lineTo(l.x2, l.y2);
  }
  ctx.stroke();

  // Wings (behind the body, translucent).
  if (geom.wings.length > 0) {
    ctx.fillStyle = WING_FILL;
    ctx.strokeStyle = WING_STROKE;
    ctx.lineWidth = Math.max(0.5, lineBase * 0.6);
    for (const w of geom.wings) {
      ellipsePath(ctx, w.cxOff, w.cyOff, w.rx, w.ry);
      ctx.fill();
      ctx.stroke();
    }
  }

  // Body segments (back→front: abdomen, thorax, head — already in that order, but
  // we draw abdomen + venom first, then thorax/armour, then head/jaws on top).
  const abd = geom.bodies[0];
  const tho = geom.bodies[1];
  const head = geom.bodies[2];

  // Abdomen.
  if (abd) {
    ctx.fillStyle = shadeColor(rgb, abd.shade);
    ellipsePath(ctx, 0, abd.cyOff, abd.rx, abd.ry);
    ctx.fill();

    // Regen → BIOLUMINESCENT body aura: a soft cyan/teal glow over the abdomen
    // (the "living tissue"), brighter with Regen. Additive so it reads as emitted
    // light. Drawn over the abdomen but UNDER the thorax/head so it haloes the gut.
    if (geom.glow.body > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const gr = Math.max(abd.rx, abd.ry) * (1.15 + 0.55 * geom.glow.body);
      paintGlow(ctx, 0, abd.cyOff, gr, BODY_GLOW_RGB, geom.glow.body);
      ctx.restore();
    }
  }

  // Venom blob at the abdomen tail. Colony-aware hue so it stays legible even on a
  // green-dominant colony body (where the default toxic-green would blend in).
  if (geom.venom) {
    // Toxicity → GLOWING venom sac: a sickly green-yellow halo UNDER the blob,
    // brightening with Toxicity, before the solid blob is drawn on top.
    if (geom.glow.venom > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const gr = geom.venom.r * (1.8 + 1.4 * geom.glow.venom);
      paintGlow(ctx, 0, geom.venom.cyOff, gr, VENOM_GLOW_RGB, geom.glow.venom);
      ctx.restore();
    }
    const venomCol = venomColorsFor(rgb);
    ctx.fillStyle = venomCol.fill;
    ctx.strokeStyle = venomCol.stroke;
    ctx.lineWidth = Math.max(0.5, lineBase * 0.7);
    ctx.beginPath();
    ctx.arc(0, geom.venom.cyOff, geom.venom.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  // Thorax.
  if (tho) {
    ctx.fillStyle = shadeColor(rgb, tho.shade);
    ellipsePath(ctx, 0, tho.cyOff, tho.rx, tho.ry);
    ctx.fill();
  }

  // Armour plate bars over the thorax (darker than the body).
  if (geom.armor.length > 0) {
    ctx.strokeStyle = shadeColor(rgb, -0.45);
    ctx.lineCap = 'round';
    for (const a of geom.armor) {
      ctx.lineWidth = a.thick;
      ctx.beginPath();
      ctx.moveTo(-a.halfW, a.cyOff);
      ctx.lineTo(a.halfW, a.cyOff);
      ctx.stroke();
    }
  }

  // Head.
  if (head) {
    ctx.fillStyle = shadeColor(rgb, head.shade);
    ellipsePath(ctx, 0, head.cyOff, head.rx, head.ry);
    ctx.fill();
  }

  // Mandibles (in front of the head).
  if (geom.mandibles.length > 0) {
    ctx.strokeStyle = shadeColor(rgb, -0.5);
    ctx.lineWidth = Math.max(0.8, lineBase * 1.1);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const m of geom.mandibles) {
      ctx.moveTo(m.x1, m.y1);
      ctx.lineTo(m.x2, m.y2);
    }
    ctx.stroke();
  }

  // Antennae (thin, on top).
  if (geom.antennae.length > 0) {
    ctx.strokeStyle = shadeColor(rgb, 0.25);
    ctx.lineWidth = Math.max(0.5, lineBase * 0.55);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const a of geom.antennae) {
      ctx.moveTo(a.x1, a.y1);
      ctx.lineTo(a.x2, a.y2);
    }
    ctx.stroke();
  }

  ctx.restore();
}
